import { createRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { rootRoute } from './__root'
import { apiClient } from '@/lib/api'
import { SequencerPage } from '@/components/sequencer/SequencerPage'
import {
  LOCAL_JAM_CONTAINER_ID,
  useSequencerStore,
} from '@/stores/sequencerStore'
import { useJam, useSequencerContainers } from '@/hooks/useSequencerProjects'

/**
 * Editor route. Handles three cases:
 *
 *   1. `$id === 'jam'` — open the per-user Jam. Separate from the
 *      containers map since Jam has its own backend row and doesn't
 *      consume a Project slot.
 *   2. `$id` matches a hydrated local container — select it.
 *   3. `$id` matches a server container that hasn't hydrated yet —
 *      wait for the sync hook's pull to complete. Only redirect to
 *      /projects if the id is genuinely missing from both local and
 *      server state.
 *
 * Previously the route would hard-redirect the moment the local
 * containers map didn't include `$id`, which meant:
 *   - Jam never opened (containers['jam'] is always undefined).
 *   - Clicking a newly-synced project from /projects bounced back
 *     to the list because the sync hook hadn't yet populated the
 *     local store.
 */
export const werkshopProjectsEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/werkshop/projects/$id',
  beforeLoad: async ({ params }) => {
    // `local-jam` is the anonymous-friendly entry point — the landing
    // page links unauthenticated visitors here so they can try the
    // sequencer with browser-local persistence. Mirrors bussboard's
    // `/bussboard/local-dev` escape hatch.
    if (params.id === LOCAL_JAM_CONTAINER_ID) return
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: ProjectEditor,
})

function ProjectEditor() {
  const params = werkshopProjectsEditorRoute.useParams() as { id: string }
  const containers = useSequencerStore(s => s.containers)
  const selectContainer = useSequencerStore(s => s.selectContainer)
  const currentContainerId = useSequencerStore(s => s.currentContainerId)
  const ensureLocalJam = useSequencerStore(s => s.ensureLocalJam)
  const navigate = useNavigate()

  const isJam = params.id === 'jam'
  const isLocalJam = params.id === LOCAL_JAM_CONTAINER_ID

  // Kick off a server fetch so we know whether `$id` is a real
  // container that simply hasn't hydrated yet vs. a bad id. Skip for
  // Jam (has its own endpoint) AND local-jam (unauth-friendly —
  // hitting the server here would 401 the anonymous visitor).
  const { data: serverContainers, isLoading: loadingList } =
    useSequencerContainers(!isJam && !isLocalJam)
  const { isLoading: loadingJam } = useJam(isJam)

  // Make sure the local-jam container exists the moment we route to
  // it — the landing-page CTA brings unauthenticated users straight
  // here, so the store needs to seed the row before SequencerPage
  // tries to read it.
  useEffect(() => {
    if (!isLocalJam) return
    ensureLocalJam()
  }, [isLocalJam, ensureLocalJam])

  const localHit = !!containers[params.id]
  const serverHit = Array.isArray(serverContainers)
    ? serverContainers.some(c => c.id === params.id)
    : false
  const stillWaitingForSync = !isJam && !isLocalJam && !localHit && loadingList

  // Once we're confident the id doesn't exist anywhere (server
  // finished loading and neither the server list nor the local map
  // has it), bounce back to the list.
  const [redirectedForMissing, setRedirectedForMissing] = useState(false)
  useEffect(() => {
    if (isJam) return
    // Local-jam is created lazily by the effect above — don't bounce
    // while the store is catching up.
    if (isLocalJam) return
    if (localHit) {
      if (params.id !== currentContainerId) {
        selectContainer(params.id)
      }
      return
    }
    if (loadingList) return
    if (serverHit) return // sync hook will hydrate on SequencerPage mount
    if (redirectedForMissing) return
    setRedirectedForMissing(true)
    navigate({ to: '/werkshop/projects' })
  }, [
    isJam,
    isLocalJam,
    localHit,
    loadingList,
    serverHit,
    params.id,
    currentContainerId,
    selectContainer,
    navigate,
    redirectedForMissing,
  ])

  // Select the local-jam container once it's in the store. Separate
  // from the default effect so we don't race the creation step.
  useEffect(() => {
    if (!isLocalJam) return
    if (!localHit) return
    if (params.id !== currentContainerId) selectContainer(params.id)
  }, [isLocalJam, localHit, params.id, currentContainerId, selectContainer])

  if (isJam) {
    if (loadingJam) {
      return (
        <div className="min-h-[100dvh] bg-surface-base grid place-items-center text-content-tertiary text-sm">
          Loading jam…
        </div>
      )
    }
    // Jam renders SequencerPage just like a regular container — the
    // user sees the same editor. Server-side Jam sync is handled by
    // the jam query hook; the editor still reads from the store's
    // `currentContainerId`, which we set to 'jam' via selectContainer
    // below.
    return <JamEditor />
  }

  if (localHit) {
    return <SequencerPage />
  }

  if (stillWaitingForSync || serverHit) {
    return (
      <div className="min-h-[100dvh] bg-surface-base grid place-items-center text-content-tertiary text-sm">
        Loading project…
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] bg-surface-base grid place-items-center text-content-tertiary text-sm">
      Project not found — returning to Projects…
    </div>
  )
}

/**
 * Opens the Jam editor. Ensures a local 'jam' container exists (so
 * SequencerPage has something to render against), then delegates to
 * the standard editor. Server sync happens via `useJam` separately.
 */
function JamEditor() {
  const containers = useSequencerStore(s => s.containers)
  const addContainer = useSequencerStore(s => s.addContainer)
  const selectContainer = useSequencerStore(s => s.selectContainer)

  useEffect(() => {
    // If no local 'jam' container yet, create one via addContainer
    // (which returns a uuid-keyed container). We then rename that
    // local id to 'jam' so the editor route's 'jam' id resolves.
    // Subsequent renders are no-ops.
    if (containers['jam']) {
      selectContainer('jam')
      return
    }
    // First-time setup: create a fresh container and use its
    // structure as Jam by renaming id → 'jam'.
    const newId = addContainer('Jam')
    useSequencerStore.getState().remapContainerId(newId, 'jam', 0)
    selectContainer('jam')
  }, [containers, addContainer, selectContainer])

  if (!containers['jam']) {
    return (
      <div className="min-h-[100dvh] bg-surface-base grid place-items-center text-content-tertiary text-sm">
        Opening jam…
      </div>
    )
  }

  return <SequencerPage />
}
